import { BundleBase, syncItemFromSelect } from '@scale/bundle-base';
import { CartAPI } from '@scale/cart-api';
import { formatMoney, shopCurrencyAmountToPresentment } from '@scale/currency';
import { calculateDiscount } from '@scale/bundle-utils';

class BundleCrossSell extends BundleBase {
  setupBundle() {
    this.on('change', this.#onChange.bind(this));
    if (this.dataset.productId) this.onVariantSelected(this.#onPageVariantSelected.bind(this));
    this.on('pageshow', this.#readControls.bind(this), { target: window });
    this.#readControls();
    if (this.getAttribute('data-auto-hide-if-in-cart') === 'true') this.#maybeHideIfAllInCart();
  }

  getBundleType() { return 'cross_sell'; }
  getBundleName() { return this.querySelector('.bundle-cross-sell__title')?.textContent.trim() || 'Bundle'; }

  getActiveItems() {
    return this.#items().filter((item) => item.dataset.available !== 'false'
      && item.querySelector('[data-cross-sell-toggle]')?.checked !== false);
  }

  getAtcButton() { return this.ref('atc'); }

  prepareCartContribution() {
    return this.authorizeOffer(this.#isFullSet() ? this.offerId : null);
  }

  submit() { this.submitStandalone(); }

  #items() { return [...this.querySelectorAll('[data-cross-sell-item]')]; }

  #isFullSet() { return this.getActiveItems().length === this.#items().length; }

  #onChange(event) {
    const select = event.target.closest?.('[data-ref="cross-sell-variant-select"]');
    if (select) this.#applyVariant(select);
    if (select || event.target.closest?.('[data-cross-sell-toggle]')) this.#update();
  }

  #readControls() {
    for (const select of this.querySelectorAll('[data-ref="cross-sell-variant-select"]')) {
      if (select.value !== select.closest('[data-cross-sell-item]').dataset.variantId) this.#applyVariant(select);
    }
    this.#update();
  }

  #applyVariant(select) {
    const item = syncItemFromSelect(select, 'data-cross-sell-item', 'item-price');
    if (item) this.#showImage(item, select.options[select.selectedIndex].dataset.image);
  }

  #onPageVariantSelected({ variant }) {
    const item = this.querySelector('[data-page-variant-ids]');
    if (!item?.dataset.pageVariantIds.split(',').includes(String(variant.id))) return;

    item.dataset.variantId = variant.id;
    item.dataset.price = variant.price;
    item.dataset.comparePrice = Math.max(variant.compare_at_price ?? 0, variant.price);
    const wasAvailable = item.dataset.available !== 'false';
    item.dataset.available = String(variant.available);
    item.querySelector('[data-ref="page-variant-title"]').textContent = variant.title;
    item.querySelector('[data-ref="item-price"]').textContent = variant.available
      ? formatMoney(variant.price)
      : Scale.translations.soldOut;

    const toggle = item.querySelector('[data-cross-sell-toggle]');
    if (toggle && wasAvailable !== variant.available) {
      toggle.disabled = !variant.available;
      toggle.checked = variant.available;
    }

    if (variant.featured_image) {
      const src = new URL(variant.featured_image.src, window.location.href);
      src.searchParams.set('width', '400');
      this.#showImage(item, src.href);
    }
    this.#update();
  }

  #showImage(item, src) {
    const img = item.querySelector('.bundle-cross-sell__img');
    if (!img || !src) return;
    img.removeAttribute('srcset');
    img.src = src;
  }

  #update() {
    const active = this.getActiveItems();
    const fullSet = this.#isFullSet();
    let subtotal = 0;
    let compareSubtotal = 0;
    for (const item of active) {
      const price = Number(item.dataset.price) || 0;
      subtotal += price;
      compareSubtotal += Number(item.dataset.comparePrice) || price;
    }

    const total = fullSet ? calculateDiscount(subtotal, this.discountType, this.discountValue) : subtotal;

    this.querySelector('.bundle-cross-sell__badge')?.toggleAttribute('data-inactive', !fullSet);
    const badge = this.ref('discount-badge');
    if (badge) {
      const amount = shopCurrencyAmountToPresentment(this.getAttribute('data-discount-value'));
      badge.hidden = amount === null;
      if (amount !== null) {
        badge.textContent = badge.dataset.labelTemplate.replace('__value__', formatMoney(Math.round(amount * 100)));
      }
    }

    const totalEl = this.ref('total-price');
    const totalText = formatMoney(total);
    if (totalEl && totalEl.textContent !== totalText) totalEl.textContent = totalText;

    const compareEl = this.ref('total-compare');
    if (compareEl) {
      const compareText = formatMoney(compareSubtotal);
      if (compareEl.textContent !== compareText) compareEl.textContent = compareText;
      if (compareEl.hidden !== compareSubtotal <= total) compareEl.hidden = compareSubtotal <= total;
    }

    const atc = this.ref('atc');
    if (atc?.dataset.labelAll) {
      atc.disabled = active.length === 0;
      if (fullSet) atc.textContent = atc.dataset.labelAll;
      else if (active.length > 1) atc.textContent = atc.dataset.labelSome.replace('__count__', active.length);
      else atc.textContent = atc.dataset.labelOne;
    }
  }

  async #maybeHideIfAllInCart() {
    const cart = await CartAPI.get();
    const variantIds = new Set(cart.items.map((i) => String(i.variant_id)));
    if (this.#items().every((item) => variantIds.has(item.dataset.variantId))) {
      this.hidden = true;
    }
  }
}

if (!customElements.get('scale-bundle-cross-sell')) {
  customElements.define('scale-bundle-cross-sell', BundleCrossSell);
}
